import React, { useEffect, useState } from 'react';
import {
  IQRCodeContentCommonProps,
  QRCodeContentNavigationActions,
  QRCodeScreenTypes,
} from './QRCodeCommonComponentEnums';
import QRCodeContent, { QRCodeContentDataRetrieveErrorCodes } from './components/QRCodeContent';
import QRCodeShareContent from './components/QRCodeShareContent';
import QRCodeRegenerateContent from './components/QRCodeRegenerateContent';
import QRCodeDownloadContent from './components/QRCodeDownloadContent';
import QRCodeFAQContent from './components/QRCodeFAQContent';

import {
  useFetchCourierConnectionCode,
  useRefreshCourierConnectionCode,
} from '@src/pages/Couriers/queries';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useCommonPersistentStore } from '@src/shared/store/common-persistent-store';
import { generateQRCodeStringData } from '@src/shared/utils';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

interface componentProps {
  initialScreenType?: QRCodeScreenTypes | null;
  onClose?: () => void;
}

const CourierQRCodeManager = ({
  initialScreenType = QRCodeScreenTypes.SHOW_QR_CODE,
  onClose,
}: componentProps) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [activeContent, setActiveContent] = React.useState<QRCodeScreenTypes | null>(
    initialScreenType
  );
  const [currentAdditionalParameters, setAdditionalParameters] = React.useState<Object>({});
  const [lastScreen, setLastScreen] = React.useState<QRCodeScreenTypes | null>(null);
  const [qrCode, setQRCode] = useState<string>('');
  const [connectionCode, setConnectionCode] = useState<string>('');
  const [connectionCodeVersion, setConnectionCodeVersion] = useState<number>(0);
  const [screenLastSeenVersions, setScreenLastSeenVersions] = useState<
    Record<QRCodeScreenTypes, number>
  >({
    [QRCodeScreenTypes.SHOW_QR_CODE]: 0,
    [QRCodeScreenTypes.SHARE_QR_CODE]: 0,
    [QRCodeScreenTypes.DOWNLOAD_QR_CODE]: 0,
    [QRCodeScreenTypes.RE_GENERATE_QR_CODE]: 0,
    [QRCodeScreenTypes.QR_CODE_FAQ]: 0,
  });
  const isInitialConnectionCodeLoad = React.useRef<boolean>(true);
  const [connectionCodeRetrieveErrorStatus, setConnectionCodeRetrieveErrorStatus] =
    useState<number>(0);
  const { restaurant } = useKeycloakUserState();
  useFetchCourierConnectionCode(restaurant?.id || '', {
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval() {
      return 15000;
    },
    onSuccess(serverResponse) {
      setConnectionCodeData(serverResponse as any);
    },
  });
  const { connectionCodeData, setConnectionCodeData } = useCommonPersistentStore();
  const [serverRequestInProgress, setServerRequestInProgress] = useState<boolean>(false);
  const refreshCourierCodeMutation = useRefreshCourierConnectionCode({
    onMutate: async () => {
      setServerRequestInProgress(true);
      setConnectionCodeRetrieveErrorStatus(0);
    },
    onSuccess() {
      queryClient.invalidateQueries('connectioncode');
      setServerRequestInProgress(false);
    },
    onError(error: any) {
      setConnectionCodeRetrieveErrorStatus(
        error?.response?.status === 429
          ? QRCodeContentDataRetrieveErrorCodes.RETRY
          : QRCodeContentDataRetrieveErrorCodes.SERVER_ERROR
      );
      setServerRequestInProgress(false);
    },
  });

  useEffect(() => {
    setActiveContent(initialScreenType);
  }, [initialScreenType]);

  useEffect(() => {
    if (connectionCodeData) {
      const { connectionCode: newConnectionCode, restaurantId } = connectionCodeData as any;
      setQRCode(generateQRCodeStringData(restaurantId, newConnectionCode));

      if (!isInitialConnectionCodeLoad.current && newConnectionCode !== connectionCode) {
        setConnectionCodeVersion(connectionCodeVersion + 1);
      }

      setConnectionCode(newConnectionCode);
      isInitialConnectionCodeLoad.current = false;
    }
  }, [connectionCodeData]);

  useEffect(() => {
    setConnectionCodeRetrieveErrorStatus(0);
  }, [activeContent]);

  //FEEDBACK HANDLING
  const usabillaPrefix = '4becb4ad73e3';
  const usabillaPositiveFeedbackAnalyticIds = [
    '3a9a81e6d5f0',
    '9eccf23d8b4f',
    '82e5dadcf134',
    '68130de252fe',
    'e4b828fa6970',
    'ba28136eb448',
    '71adcd0df5ac',
    '9f6cdef5a8d6',
    '6845a32ae59c',
    '3d508b28dcbc',
    'f00e0a21aec4',
    'd35fdc69567c',
    'fc4e664f3147',
    '1739690b1d26',
  ];
  const usabillaNegativeFeedbackAnalyticIds = [
    'c6065d47d67d',
    '2f172bd9f8f9',
    '313a009a5754',
    '31f81bb489c0',
    '3c1ac349f885',
    '28ed80849b66',
    'b9926a1fac8d',
    '26c08b791be4',
    '58eab164856a',
    '424df157344f',
    'ce2c524e3ba4',
    '25deb75b6ebd',
    '7dabcaa15acf',
    'bd0e94657b0d',
  ];

  const clearUsabillaRecords = () => {
    [...usabillaPositiveFeedbackAnalyticIds, ...usabillaNegativeFeedbackAnalyticIds].forEach(
      (usabillaAnalyticId) => {
        localStorage.removeItem(`usbl.${usabillaPrefix}.c.${usabillaAnalyticId}`);
      }
    );
  };

  const fillTargetUsabillaRecords = (analyticIds: Array<string>) => {
    analyticIds.forEach((usabillaAnalyticId) => {
      localStorage.setItem(`usbl.${usabillaPrefix}.c.${usabillaAnalyticId}`, 'T//////');
    });
  };

  const onPositiveFeedbackSelected = () => {
    fillTargetUsabillaRecords(usabillaNegativeFeedbackAnalyticIds);
    window.usabilla_live('virtualPageView');
    window.usabilla_live('trigger', `MCP-1973_positive_${i18n.language}`);
    clearUsabillaRecords();
  };

  const onNegativeFeedbackSelected = () => {
    fillTargetUsabillaRecords(usabillaPositiveFeedbackAnalyticIds);
    window.usabilla_live('virtualPageView');
    window.usabilla_live('trigger', `MCP-1973_negative_${i18n.language}`);
    clearUsabillaRecords();
  };

  useEffect(() => {
    if (activeContent === QRCodeScreenTypes.QR_CODE_FAQ) {
      clearUsabillaRecords();
    }
  }, [activeContent]);
  //FEEDBACK HANDLING
  const handleOnNavigate = (
    action: QRCodeContentNavigationActions,
    screenType?: QRCodeScreenTypes,
    additionalParameters?: Object
  ) => {
    if (action === QRCodeContentNavigationActions.CLOSE) {
      onClose && onClose();
    }
    if (action === QRCodeContentNavigationActions.JUMP_SCREEN && screenType) {
      if (additionalParameters) {
        setAdditionalParameters(additionalParameters);
      } else {
        setAdditionalParameters({});
      }
      setActiveContent(screenType);
      setLastScreen(activeContent);
    }
    if (action === QRCodeContentNavigationActions.JUMP_LAST_VISITED_SCREEN && lastScreen) {
      setActiveContent(lastScreen);
      setLastScreen(null);
    }
  };

  const handleOnCodeRegenerate = async () => {
    if (connectionCodeData && typeof connectionCodeData !== 'boolean') {
      setConnectionCodeRetrieveErrorStatus(0);

      await refreshCourierCodeMutation.mutate({
        courierConnectionData: connectionCodeData,
        restaurantId: restaurant?.id,
      });
    }
  };
  const commonPropsForContentScreens: IQRCodeContentCommonProps = {
    onNavigate: handleOnNavigate,
    bottomSheetView: true,
    popupView: true,
    additionalParameters: currentAdditionalParameters,
  };

  const handleVersionAcknowledged = (screen: QRCodeScreenTypes) => {
    setScreenLastSeenVersions({
      ...screenLastSeenVersions,
      [screen]: connectionCodeVersion,
    });
  };
  const qrCodeScreenContentMap: Record<QRCodeScreenTypes, React.ReactNode> = {
    [QRCodeScreenTypes.SHOW_QR_CODE]: (
      <QRCodeContent
        dataRetrieveErrorCode={connectionCodeRetrieveErrorStatus}
        onCodeRegenerate={handleOnCodeRegenerate}
        qrCode={qrCode}
        connectionCode={connectionCode}
        connectionCodeVersion={connectionCodeVersion}
        lastSeenVersion={screenLastSeenVersions[QRCodeScreenTypes.SHOW_QR_CODE]}
        onVersionAcknowledged={() => handleVersionAcknowledged(QRCodeScreenTypes.SHOW_QR_CODE)}
        isComponentBusy={serverRequestInProgress}
        {...commonPropsForContentScreens}
      />
    ),
    [QRCodeScreenTypes.SHARE_QR_CODE]: (
      <QRCodeShareContent connectionCode={connectionCode} {...commonPropsForContentScreens} />
    ),
    [QRCodeScreenTypes.DOWNLOAD_QR_CODE]: (
      <QRCodeDownloadContent
        qrCode={qrCode}
        connectionCode={connectionCode}
        connectionCodeVersion={connectionCodeVersion}
        lastSeenVersion={screenLastSeenVersions[QRCodeScreenTypes.DOWNLOAD_QR_CODE]}
        onVersionAcknowledged={() => handleVersionAcknowledged(QRCodeScreenTypes.DOWNLOAD_QR_CODE)}
        {...commonPropsForContentScreens}
      />
    ),
    [QRCodeScreenTypes.RE_GENERATE_QR_CODE]: (
      <QRCodeRegenerateContent {...commonPropsForContentScreens} />
    ),
    [QRCodeScreenTypes.QR_CODE_FAQ]: (
      <QRCodeFAQContent
        onPositiveFeedback={onPositiveFeedbackSelected}
        onNegativeFeedback={onNegativeFeedbackSelected}
        {...commonPropsForContentScreens}
      />
    ),
  };

  return <>{activeContent ? qrCodeScreenContentMap[activeContent] : null}</>;
};

export default CourierQRCodeManager;
